import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * Formats a Date object to the API-expected format (Y-m-d H:i:s).
 * Uses dayjs for consistent date handling across the application.
 *
 * @param date - The Date object to format
 * @returns A string in the format "yyyy-MM-dd HH:mm:ss"
 */
export const formatDateForApi = (date: Date): string => {
  return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Parses a date string that can be in either ISO format (2024-12-20T12:00:00Z)
 * or SQL format (2024-12-20 12:00:00). This handles both existing data and
 * newly created closures.
 *
 * @param dateString - The date string to parse
 * @returns A dayjs object (may be invalid if parsing fails)
 */
export const parseDateString = (dateString: string): Dayjs => {
  // Try SQL format first (new format: "YYYY-MM-DD HH:mm:ss")
  const sqlDate = dayjs(dateString, "YYYY-MM-DD HH:mm:ss", true);
  if (sqlDate.isValid()) {
    return sqlDate;
  }

  // Fall back to ISO format (existing data: "YYYY-MM-DDTHH:mm:ssZ")
  const isoDate = dayjs(dateString);
  if (isoDate.isValid()) {
    return isoDate;
  }

  // Log warning for debugging if neither format works
  console.warn(`Invalid date string: ${dateString}`);
  return dayjs(null); // Returns an invalid dayjs object
};
